"use client"

import { useEffect, useRef } from "react"
import Link from "next/link"
import { Typography } from "@/components/typography"
import { TkMonogram } from "@/components/ui/TkMonogram"
import type { MicroSiteLink } from "./Header"

const navLinks: { href: string; label: string; desc?: string; sub?: boolean; rule?: boolean }[] = [
  { href: "/services/web-development", label: "WEBSITE DEVELOPMENT" },
  { href: "/services/app-development", label: "APP DEVELOPMENT" },
  { href: "/services/systems-integration", label: "INTEGRATIONS" },
  { href: "/services/ai-hivemind", label: "AGENTIC AI" },
  { href: "/portfolio", label: "WHAT I'VE DONE", rule: true },
  { href: "/portfolio/demos", label: "Demos", sub: true },
  { href: "/why", label: "WHY CHOOSE ME" },
  { href: "/how-it-works", label: "HOW IT WORKS" },
]

interface MobileNavProps {
  isOpen: boolean
  onClose: () => void
  /* Service micro-site anchors; when present they render as an "On this page"
     section above the main links. */
  micrositeLinks?: MicroSiteLink[]
}

/**
 * CSS-transition drawer (no framer-motion) so the animation library stays out
 * of the base bundle on every page — it only loads where sections use it.
 */
export function MobileNav({ isOpen, onClose, micrositeLinks }: MobileNavProps) {
  /* In-page anchors can't use default navigation here: the body is
     position:fixed while the drawer is open, so a hash jump can't land, and
     the close cleanup then restores the pre-open scroll position on top of
     it. Instead the click records the target and closes; the unlock cleanup
     itself performs the scroll — deterministic, no timer/rAF races.
     scrollIntoView honors the sections' scroll-mt, clearing the sticky bar. */
  const pendingAnchorRef = useRef<string | null>(null)

  // Lock body scroll when nav is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY
      // Lock body scroll
      document.body.style.position = "fixed"
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = "100%"
      document.body.style.overflow = "hidden"

      return () => {
        document.body.style.position = ""
        document.body.style.top = ""
        document.body.style.width = ""
        document.body.style.overflow = ""
        const anchor = pendingAnchorRef.current
        if (anchor) {
          pendingAnchorRef.current = null
          document.querySelector(anchor)?.scrollIntoView()
          history.replaceState(null, "", anchor)
        } else {
          // Restore scroll position when nav closes
          window.scrollTo(0, scrollY)
        }
      }
    }
  }, [isOpen])

  const handleAnchorClick = (event: React.MouseEvent, href: string) => {
    event.preventDefault()
    pendingAnchorRef.current = href
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 bg-tk-onyx/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Menu */}
      <div
        aria-hidden={!isOpen}
        className={`fixed right-0 top-0 w-80 bg-tk-linen border-l border-tk-slate/10 shadow-xl z-50 transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full invisible"
        }`}
        style={{ height: "100dvh", maxHeight: "100dvh", transitionProperty: "transform, visibility" }}
      >
        {/* Scrollable content area with bottom padding for button */}
        <div className="overflow-y-auto overscroll-contain pb-24" style={{ height: "100%" }}>
          <div className="p-6 space-y-6">
            {/* Logo and Close button */}
            <div className="flex items-center justify-between">
              <Link href="/" onClick={onClose} className="group flex items-center gap-1.5">
                <TkMonogram className="h-8 w-8" />
                <span className="tk-nav-grow origin-left text-sm font-semibold uppercase tracking-[0.15em] text-tk-teal font-ui">
                  TALLKAROL
                </span>
              </Link>
              <button
                onClick={onClose}
                className="p-2 hover:bg-tk-slate/5 rounded-lg transition-colors"
                aria-label="Close menu"
              >
                <svg
                  className="w-6 h-6 text-tk-slate"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <nav className="space-y-1">
              {micrositeLinks && (
                <>
                  {micrositeLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(event) => handleAnchorClick(event, link.href)}
                      className="block px-4 py-3 rounded-lg hover:bg-tk-slate/5 transition-colors"
                    >
                      <Typography
                        variant="body"
                        className="font-medium text-tk-onyx font-ui uppercase tracking-[0.05em]"
                      >
                        {link.label}
                      </Typography>
                    </a>
                  ))}
                  <div aria-hidden="true" className="mx-4 my-3 h-px bg-tk-slate/15" />
                </>
              )}
              {navLinks.map((link) => (
                <div key={link.href}>
                  {link.rule && (
                    <div
                      aria-hidden="true"
                      className="mx-4 my-3 h-px bg-tk-slate/15"
                    />
                  )}
                  <Link
                    href={link.href}
                    prefetch={link.href.startsWith("/services")}
                    onClick={onClose}
                    className={`block px-4 py-3 rounded-lg hover:bg-tk-slate/5 transition-colors ${
                      link.sub ? "pl-8 text-sm" : ""
                    }`}
                  >
                    <Typography
                      variant={link.sub ? "body-sm" : "body"}
                      className={`font-medium text-tk-onyx font-ui ${
                        link.sub ? "text-xs" : "uppercase tracking-[0.05em]"
                      }`}
                    >
                      {link.label}
                    </Typography>
                    {link.desc && (
                      <span className="block text-xs text-tk-slate/70 font-body">
                        {link.desc}
                      </span>
                    )}
                  </Link>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Book a Call button - absolutely positioned at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-tk-slate/10 bg-tk-linen">
          <Link
            href="/contact#intake"
            prefetch={false}
            onClick={onClose}
            data-track="cta"
            data-track-id="mobile-get-in-touch"
            data-track-loc="mobile-nav"
            className="block w-full rounded-full bg-tk-teal px-6 py-3 text-center text-sm font-semibold uppercase tracking-[0.05em] text-tk-linen hover:bg-tk-teal/90 transition-colors font-ui"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </>
  )
}
